import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { PRINCIPALE, contesto, useTraduzioni } from '../../i18n';
import { API, axios } from '../../utils/api';
import { salvaBlob } from '../../utils/documenti';
import { formattaRoma } from '../../utils/percorso';
import './privacy.css';

// "Scarica i miei dati": uno zip con i dati (JSON) e i documenti, preparato in sottofondo. Quando è pronto arriva
// un'email; si scarica da qui, con il proprio accesso, entro 24 ore. Usato nel profilo del cliente.

// La scadenza nell'ora di Roma (come l'email): prima nell'ora del telefono. In italiano le parole di sempre;
// nelle altre lingue la stessa data scritta da Intl (i18n), sempre nell'ora di Roma.
export const ora = (v, lingua = PRINCIPALE) => (lingua === PRINCIPALE
  ? formattaRoma(v, { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
  : contesto(lingua).data(v, { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }));

export const useIMieiDati = (clienteId) => {
  const { t } = useTraduzioni();
  const [stato, setStato] = useState(null);
  const [attesa, setAttesa] = useState(false);
  const leggi = useCallback(() => axios.get(`${API}/clienti/${clienteId}/esportazione`).then((r) => setStato(r.data)).catch(() => {}), [clienteId]);
  useEffect(() => { if (clienteId) leggi(); }, [clienteId, leggi]);
  useEffect(() => {  // mentre si prepara, si ricontrolla ogni tanto
    if (stato?.stato !== 'in_preparazione') return undefined;
    const t = setTimeout(leggi, 4000);
    return () => clearTimeout(t);
  }, [stato, leggi]);
  // Il messaggio dipende da cosa risponde il server (prima "Li stiamo preparando" in verde anche quando la risposta
  // era l'esportazione andata in errore, insieme a "Non siamo riusciti a prepararli")
  const chiedi = async () => {
    setAttesa(true);
    try {
      const dati = (await axios.post(`${API}/clienti/${clienteId}/esportazione`)).data;
      setStato(dati);
      const [tipo, chiave] = MESSAGGI_RICHIESTA[dati?.stato] || MESSAGGI_RICHIESTA.errore;
      toast[tipo](t(chiave));
    } catch (err) {
      toast.error(messaggioDelServer(err) || t('profilo.dati.non_chiesti'));
    } finally { setAttesa(false); }
  };
  // Uno scarico alla volta: con il doppio clic partivano due download (e due voci «esportazione_scaricata» per lo
  // staff). Il ref ferma il secondo clic subito, prima che React spenga il bottone
  const scaricando = useRef(false);
  const [inScarico, setInScarico] = useState(false);
  // Un errore sullo scarico non è sempre "il link non vale più": dipende dalla risposta
  const scarica = async () => {
    if (scaricando.current) return;
    scaricando.current = true;
    setInScarico(true);
    try {
      const r = await axios.get(`${API}/esportazioni/${stato.id}/scarica`, { responseType: 'blob' });
      salvaBlob(r.data, 'i-miei-dati-mkclient.zip');
    } catch (err) {
      const codice = err?.response?.status;
      toast.error(messaggioScarico(codice, t));
      if (codice && codice < 500) leggi(); // lo stato è cambiato (scaduto, non pronto, tolto): si rilegge
    } finally {
      scaricando.current = false;
      setInScarico(false);
    }
  };
  return { stato, attesa, chiedi, scarica, inScarico };
};

const MESSAGGI_RICHIESTA = {
  in_preparazione: ['success', 'profilo.dati.msg_preparazione'],
  pronta: ['success', 'profilo.dati.msg_pronta'],
  scaduta: ['error', 'profilo.dati.msg_scaduta'],
  errore: ['error', 'profilo.dati.msg_errore'],
};

// 410 scaduto, 409 non ancora pronto, 404 zip non più disponibile, 403 non vostro; rete e 5xx: il servizio.
// `t` è quello della lingua di chi guarda; senza, l'italiano.
export const messaggioScarico = (codice, t = contesto(PRINCIPALE).t) => {
  if (codice === 410) return t('profilo.dati.scarico_410');
  if (codice === 409) return t('profilo.dati.scarico_409');
  if (codice === 404) return t('profilo.dati.scarico_404');
  if (codice === 403) return t('profilo.dati.scarico_403');
  return t('profilo.dati.scarico_servizio');
};

const messaggioDelServer = (err) => {
  const codice = err?.response?.status;
  const detail = err?.response?.data?.detail;
  return codice && codice < 500 && typeof detail === 'string' ? detail : null;
};

const ScaricaIMieiDati = ({ clienteId }) => {
  const { t, l: lingua } = useTraduzioni();
  const { stato, attesa, chiedi, scarica, inScarico } = useIMieiDati(clienteId);
  const pronta = stato?.stato === 'pronta';
  return (
    <div className="dati-privacy" data-testid="scarica-i-miei-dati">
      <b>{t('profilo.dati.titolo')}</b>
      <p>{t('profilo.dati.spiega')}</p>
      {stato?.stato === 'in_preparazione' && <p className="stato">{t('profilo.dati.in_preparazione')}</p>}
      {pronta && <p className="stato">{t('profilo.dati.pronti', { quando: ora(stato.scade, lingua) })}</p>}
      {stato?.stato === 'errore' && <p className="stato no">{t('profilo.dati.errore')}</p>}
      <div className="bottoni">
        {/* mentre si scarica: spento, con la rotella (.btn.busy di cliente.css); il doppio clic non seleziona il testo
            (si vedeva un riquadro grigio sotto la rotella) */}
        {pronta && (
          <button type="button" className={`btn p${inScarico ? ' busy' : ''}`} onClick={scarica} disabled={inScarico} aria-busy={inScarico}
            style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
            {t('profilo.dati.scarica')}
          </button>
        )}
        {!pronta && <button type="button" className="btn" onClick={chiedi} disabled={attesa || stato?.stato === 'in_preparazione'}>{t(stato?.stato === 'scaduta' ? 'profilo.dati.di_nuovo' : 'profilo.dati.chiedete')}</button>}
      </div>
    </div>
  );
};

export default ScaricaIMieiDati;
